import { Component, effect, EventEmitter, Input, OnDestroy, OnInit, Output, signal, ViewChild } from '@angular/core';
import { ChatChannel } from '@app/model/chat-channel';
import { AppSyncChatMessage, ChatService } from '@app/services/chat.service';
import { SettingsService } from '@app/services/settings.service';
import { ImageModel, ProfileService } from 'library-explorer';
import { NgScrollbar } from 'ngx-scrollbar';
import { Subject } from 'rxjs';
import { delay, finalize, takeUntil } from 'rxjs/operators';
import { AppSyncChatAdapter } from '../adapters/appsync-chat.adapter';

const MESSAGES_PAGE_SIZE = 50;
const LOAD_MORE_SCROLL_THRESHOLD_PX = 100;

@Component({
  selector: 'app-chat-channel-messages-list-v2',
  templateUrl: './chat-channel-messages-list-v2.component.html',
  styleUrls: ['./chat-channel-messages-list-v2.component.scss'],
  providers: [AppSyncChatAdapter],
  standalone: false
})
export class ChatChannelMessagesListV2Component implements OnInit, OnDestroy {
  @Output() public edit: EventEmitter<AppSyncChatMessage> = new EventEmitter();
  @Output() public markAsRead: EventEmitter<void> = new EventEmitter();

  @Input() public set groupChannel(groupChannel: ChatChannel) {
    this._groupChannel = groupChannel;
    this.initializeChannel();
  }

  public get groupChannel() {
    return this._groupChannel;
  }

  @ViewChild(NgScrollbar, { static: false }) public set scrollbarRef(value: NgScrollbar) {
    if (value && value !== this._scrollbarRef) {
      this._scrollbarRef = value;
      this.scrolbarReady.set(true);
    }
  }

  public get scrollbarRef() {
    return this._scrollbarRef;
  }

  public messages = signal<Array<AppSyncChatMessage>>([]);
  public currentUserId: string;
  public canAdministerChannel = false;
  public isLoading = false;
  public globalChatLogo: ImageModel;

  public hasScroll = false;
  public loadingIndicator = false;

  public _groupChannel: ChatChannel;

  private scrollPosition = 0;
  private nextToken: string | undefined;
  private hasMore = true;
  private readonly messageIds = new Set<string>();
  private readonly channelChange$ = new Subject<void>();
  private readonly unsubscribe: Subject<void> = new Subject();
  private _scrollbarRef: NgScrollbar;

  private readonly scrolbarReady = signal(false);

  constructor(
    private readonly settingsService: SettingsService,
    private readonly profileService: ProfileService,
    private readonly chatService: ChatService,
    public readonly chatAdapter: AppSyncChatAdapter,
  ) {
    effect(() => {
      this.messages();

      if (!this.scrolbarReady()) {
        return;
      }

      queueMicrotask(() => {
        this.scrollUpdated();
      });
    });
  }

  ngOnInit() {
    this.currentUserId = this.profileService.getCurrentProfileValue()?.id;
    this.canAdministerChannel = this.profileService.isCurrentUserIsAdmin();

    this.settingsService.getSettings()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(data => this.globalChatLogo = data.general.platformLogo);
  }

  ngOnDestroy(): void {
    this.channelChange$.next();
    this.channelChange$.complete();
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  public deleteMessage(_item: AppSyncChatMessage): void {
    // no-op: message deletion not supported by ChatService
  }

  public editMessage(item: AppSyncChatMessage): void {
    this.edit.emit(item);
  }

  public scrollUpdated(): void {
    if (!this.scrollbarRef) {
      return;
    }

    this.scrollbarRef.scrollTo({ bottom: this.scrollPosition, duration: 0 });
    this.hasScroll = this.scrollbarRef.adapter.isVerticallyScrollable();
  }

  public closeChannel(): void {
    // no-op: channel management not supported by ChatService
  }

  public scrollToBottom(): void {
    setTimeout(() => {
      this.scrollbarRef.scrollTo({ bottom: 0, duration: 0 });
      this.scrollbarRef.update();
    }, 50);
  }

  public onScroll(event: Event): void {
    if (this.isLoading || !this.hasMore) {
      return;
    }

    const el = event.target as HTMLElement;

    const scrollTop = el.scrollTop;
    const distanceToBottom = el.scrollHeight - el.offsetHeight - scrollTop;

    this.scrollPosition = distanceToBottom;

    if (scrollTop < 100) {
      this.loadMessages(this.groupChannel?.url);
    }
  }

  private initializeChannel(): void {
    this.channelChange$.next();
    this.messages.set([]);
    this.messageIds.clear();
    this.scrollPosition = 0;
    this.nextToken = undefined;
    this.hasMore = true;

    const channelUrl = this._groupChannel?.url;
    if (!channelUrl) {
      return;
    }

    this.markAsRead.emit();
    this.loadMessages(channelUrl);
    this.subscribeToNewMessages(channelUrl);
  }

  private loadMessages(channelUrl: string): void {
    if (this.isLoading || !this.hasMore) {
      return;
    }

    this.isLoading = true;
    this.chatService.getMessages(channelUrl, { limit: MESSAGES_PAGE_SIZE, nextToken: this.nextToken }).pipe(
      finalize(() => this.isLoading = false),
      takeUntil(this.channelChange$),
      takeUntil(this.unsubscribe),
    ).subscribe(({ items, nextToken }) => {
      this.prependMessages(items);
      this.nextToken = nextToken;
      this.hasMore = !!nextToken;
    });
  }

  private subscribeToNewMessages(channelUrl: string): void {
    this.chatService.subscribeToNewMessages(channelUrl).pipe(
      delay(200),
      takeUntil(this.channelChange$),
      takeUntil(this.unsubscribe),
    ).subscribe(msg => {
      this.appendMessage(msg);
      this.markAsRead.emit();
      this.scrollToBottom();
    });
  }

  private appendMessage(msg: AppSyncChatMessage): void {
    if (!this.messageIds.has(msg.messageId)) {
      this.messageIds.add(msg.messageId);
      this.messages.update(value => [...value, msg]);
    }
  }

  private prependMessages(items: AppSyncChatMessage[]): void {
    const fresh = items.filter(m => !this.messageIds.has(m.messageId));
    fresh.forEach(m => this.messageIds.add(m.messageId));

    if (fresh.length) {
      this.messages.update(value => [...fresh, ...value]);
    }
  }
}
